import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Broadcast } from '@phosphor-icons/react';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { useRelativeTime } from '../lib/time';
import { getGateway, listGateways, isNotDeployed, fmtMac } from '../lib/gateways';
import { GatewayStatusChip } from '../components/gateways/GatewayStatusChip';

const Tile = ({ label, children, testid }) => (
  <div className="rounded-xl border border-stone_line p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold">{label}</div>
    <div className="text-base text-ink font-medium mt-1 tabular-nums">{children}</div>
  </div>
);

function HeardDevices({ g, t, rel, pending }) {
  const list = g.heard_devices;
  return (
    <section className="bg-white rounded-2xl border border-stone_line p-6 shadow-soft" data-testid="gateway-heard">
      <h2 className="font-heading text-xl text-ink mb-1">{t('gw_heard_title')}</h2>
      <p className="text-xs text-ink-muted mb-4">{t('gw_heard_sub')}</p>
      {pending || !Array.isArray(list) ? (
        <div className="bg-bg-alt rounded-xl p-6 text-center text-sm text-ink-soft" data-testid="gateway-heard-pending">{t('gw_backend_pending')}</div>
      ) : list.length === 0 ? (
        <div className="text-sm text-ink-muted" data-testid="gateway-heard-empty">{t('gw_heard_none')}</div>
      ) : (
        <ul className="divide-y divide-stone_line">
          {list.map((d) => (
            <li key={d.device_uuid} className="py-3 grid grid-cols-1 gap-1 sm:flex sm:items-center sm:gap-4 text-sm" data-testid={`gateway-heard-${d.device_uuid}`}>
              <Link to={`/devices/${d.device_uuid}`} className="text-ink font-medium hover:text-sage-700 min-w-[160px]">{d.device_name || d.type}<span className="text-[11px] text-ink-muted font-normal ml-2 uppercase">{d.device_name ? d.type : ''}</span></Link>
              <span className="text-xs text-ink-soft">{[d.room_name, d.resident_name].filter(Boolean).join(' · ') || '—'}</span>
              <span className="text-xs text-ink-muted sm:ml-auto">{d.rssi_best_latest != null ? `${d.rssi_best_latest} dBm` : '—'}{d.rssi_avg != null ? ` (${t('gw_avg')} ${d.rssi_avg})` : ''} · {d.samples_24h ?? '—'} {t('gw_samples_short')}</span>
              <span className="text-xs text-ink-soft sm:w-24 sm:text-right">{d.last_heard_at ? rel(d.last_heard_at) : '—'}</span>
            </li>
          ))}
        </ul>
      )}
      {Array.isArray(g.unregistered_heard) && g.unregistered_heard.length > 0 && (
        <div className="mt-5 pt-4 border-t border-stone_line text-xs text-ink-soft" data-testid="gateway-heard-unregistered">
          <div className="font-semibold text-ink mb-2">{t('gw_heard_unknown')}</div>
          {g.unregistered_heard.map((u) => <span key={u.mac} className="inline-block font-mono mr-4">{fmtMac(u.mac)}</span>)}
        </div>
      )}
    </section>
  );
}

export default function GatewayDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const rel = useRelativeTime();
  const { user } = useAuth();
  const isSuper = user?.is_super_admin === true;
  const [g, setG] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    getGateway(id, user?.is_super_admin === true ? null : undefined).then((x) => alive && setG(x)).catch((e) => {
      if (!isNotDeployed(e)) { if (alive) setError(true); return; }
      // Detail route not deployed yet: fall back to the list row.
      setPending(true);
      listGateways().then((d) => {
        const row = d.gateways.find((x) => String(x.gateway_id) === String(id));
        if (alive) (row ? setG(row) : setError(true));
      }).catch((e2) => alive && setError(e2?.response?.status === 403 ? 'scope' : true));
    });
    return () => { alive = false; };
  }, [id, user?.is_super_admin]);

  if (error) return <div className="bg-bg-alt rounded-2xl p-10 text-center text-ink-soft" data-testid="gateway-detail-error">{t(error === 'scope' ? 'gw_scope_pending' : 'gw_not_found')}</div>;
  if (!g) return <div className="text-ink-muted">{t('loading')}</div>;
  const pctTxt = g.discard_rate == null ? '—' : `${Math.round(g.discard_rate * 100)}%`;

  return (
    <div className="space-y-6" data-testid="gateway-detail">
      <Link to="/devices?type=gateways" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-ink" data-testid="gateway-back"><ArrowLeft size={14} />{t('nav_devices')}</Link>
      <header className="bg-bg-alt rounded-2xl border border-stone_line p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-sage-100 text-sage-600 grid place-items-center"><Broadcast size={24} weight="duotone" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold">{t('gw_one')}</div>
              <h1 className="font-heading text-3xl sm:text-4xl text-ink" data-testid="gateway-detail-name">{g.gateway_name || '—'}</h1>
              <div className="text-xs text-ink-soft mt-1 font-mono">{isSuper && `${fmtMac(g.gateway_mac_hex)} · `}{g.gateway_hw_type || '—'}</div>
            </div>
          </div>
          <GatewayStatusChip status={g.status} testid="gateway-detail-status" />
        </div>
        <div className="mt-4 text-sm text-ink-soft" data-testid="gateway-detail-assignment">
          {g.organization_id ? (
            <>{g.organization_name} · {g.room_id ? <Link to={`/rooms/${g.room_id}`} className="text-ink hover:text-sage-700 underline-offset-2 hover:underline">{g.room_name}</Link> : '—'}</>
          ) : t('gw_unassigned')}
          {g.notes && <p className="mt-2 text-xs text-ink-muted whitespace-pre-line">{g.notes}</p>}
          {user?.role === 'admin' && <Link to="/admin/gateways" className="block mt-3 text-xs text-sage-700 hover:underline" data-testid="gateway-detail-manage">{t('gw_manage_link')}</Link>}
        </div>
      </header>
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="gateway-health">
        {isSuper && <Tile label={t('gw_last_message')} testid="gateway-health-last">{g.last_message_at ? rel(g.last_message_at) : t('gw_never')}</Tile>}
        {isSuper && <Tile label={t('gw_col_msgs')} testid="gateway-health-msgs">{g.messages_24h ?? '—'}</Tile>}
        {isSuper && <Tile label={t('gw_col_discard')} testid="gateway-health-discard">{pctTxt}</Tile>}
        <Tile label={t('gw_devices_seen')} testid="gateway-health-devices">{g.devices_seen_latest ?? '—'}{g.devices_seen_max_24h != null && <span className="text-xs text-ink-muted"> / {g.devices_seen_max_24h} max</span>}</Tile>
        <Tile label={t('gw_rssi_range')} testid="gateway-health-rssi">{g.rssi_min != null ? `${g.rssi_min} … ${g.rssi_max} dBm` : '—'}</Tile>
        <Tile label={t('gw_hours_active')} testid="gateway-health-hours">{g.hours_active ?? '—'}</Tile>
        <Tile label={t('gw_longest_gap')} testid="gateway-health-gap">{g.max_gap_hours != null ? `${g.max_gap_hours} h` : '—'}</Tile>
        <Tile label={t('gw_config_push')} testid="gateway-health-config">{g.config?.last_push_at ? rel(g.config.last_push_at) : t('gw_never')}</Tile>
      </section>
      <HeardDevices g={g} t={t} rel={rel} pending={pending} />
    </div>
  );
}
